import { forwardRef, useCallback, useImperativeHandle, useLayoutEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { ArrowUp, Paperclip, Square } from 'lucide-react';
import type { Attachment } from '@medicail/shared';
import { cn } from '@/lib/cn';
import { IconButton } from '@/components/ui';
import { ComposerChip } from './AttachmentChip';
import { useAttachments } from './useAttachments';

export interface ComposerHandle {
  addFiles(files: File[]): void;
  focus(): void;
}

export const DISCLAIMER = 'MedicAiL is not a substitute for a doctor. In an emergency call your local emergency number.';

export const Composer = forwardRef<
  ComposerHandle,
  {
    streaming: boolean;
    disabled?: boolean;
    onSend: (input: { content: string; attachments: Attachment[] }) => void;
    onStop: () => void;
    placeholder?: string;
  }
>(function Composer({ streaming, disabled, onSend, onStop, placeholder = 'Ask a health question' }, ref) {
  const [text, setText] = useState('');
  const taRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const att = useAttachments();

  useImperativeHandle(ref, () => ({ addFiles: att.addFiles, focus: () => taRef.current?.focus() }), [att.addFiles]);

  // Auto-grow up to ~8 lines.
  useLayoutEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 208)}px`;
  }, [text]);

  const canSend = !disabled && !streaming && !att.uploading && (text.trim().length > 0 || att.items.some((i) => i.status === 'done'));

  const submit = useCallback(() => {
    if (!canSend) return;
    const attachments = att.consume();
    const content = text.trim();
    setText('');
    onSend({ content, attachments });
  }, [canSend, att, text, onSend]);

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const files = Array.from(e.clipboardData.files);
    if (files.length) {
      e.preventDefault();
      att.addFiles(files);
    }
  };

  return (
    <div
      className={cn(
        'rounded-[1.75rem] border border-line bg-surface p-2 shadow-lift transition-[border-color,box-shadow] focus-within:border-royal-300 focus-within:ring-4 focus-within:ring-royal-500/15',
      )}
    >
      {att.items.length > 0 && (
        <ul className="flex flex-wrap gap-2 px-2 pb-2 pt-1" aria-label="Attachments">
          {att.items.map((i) => (
            <ComposerChip key={i.localId} {...i} onRemove={() => att.remove(i.localId)} />
          ))}
        </ul>
      )}
      <div className="flex items-end gap-1.5">
        <input
          ref={fileRef}
          type="file"
          multiple
          hidden
          accept={att.accept}
          onChange={(e) => {
            att.addFiles(Array.from(e.target.files ?? []));
            e.target.value = '';
          }}
        />
        <IconButton
          label={att.count >= att.maxFiles ? `Up to ${att.maxFiles} files per message` : 'Attach files'}
          disabled={att.count >= att.maxFiles}
          onClick={() => fileRef.current?.click()}
          className="size-11 rounded-full text-ink-muted"
        >
          <Paperclip className="size-5" />
        </IconButton>
        <textarea
          ref={taRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          rows={1}
          maxLength={20000}
          placeholder={placeholder}
          aria-label="Message MedicAiL"
          enterKeyHint="send"
          className="scroll-thin max-h-52 min-h-11 flex-1 resize-none bg-transparent px-2 py-[0.6rem] text-base leading-6 text-ink placeholder:text-ink-muted/80 focus:outline-none focus-visible:outline-none"
        />
        {streaming ? (
          <IconButton label="Stop generating" variant="navy" onClick={onStop} className="size-11 rounded-full">
            <Square className="size-4 fill-current" />
          </IconButton>
        ) : (
          <IconButton
            label={att.uploading ? 'Waiting for uploads to finish' : 'Send message'}
            variant="primary"
            disabled={!canSend}
            onClick={submit}
            className="size-11 rounded-full"
          >
            <ArrowUp className="size-5" />
          </IconButton>
        )}
      </div>
    </div>
  );
});
